'use client';

import { createPortal } from 'react-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckSquare, Loader2, UserPlus } from 'lucide-react';
import { authService } from '@/lib/authService';
import { useTheme } from '@/contexts/ThemeContext';
import { isCapacitor } from '@/lib/platformUtils';
import CapacitorModalFilterHeader, {
  ModalFilterBar,
} from '@/components/CapacitorModalFilterHeader';
import { cn } from '@/lib/utils';

type ContactPickerTab = 'crm' | 'company' | 'personal';

export interface EmailContactPickerContact {
  id: string;
  email: string;
  name?: string;
  company?: string;
  job_title?: string;
  contact_type?: string;
  avatar_url?: string;
  source: 'crm' | 'company' | 'personal';
}

interface EmailContactPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddRecipients: (recipients: Array<{ email: string; name?: string }>) => void;
  title?: string;
  /** When set, replaces the default subtitle below the title (e.g. calendar invites vs email). */
  description?: string;
  /** Replaces the grey hint in the footer when nothing is selected. */
  footerIdleHint?: string;
  /** Label for the primary action (default: Add recipients). */
  addButtonLabel?: string;
  defaultTab?: ContactPickerTab;
  allowedTabs?: ContactPickerTab[];
}

/** Loose shape of the contact payloads returned by /api/v1/email-contacts. */
interface RawEmailContact {
  id: string | number;
  email: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  company?: string;
  job_title?: string;
  contact_type?: string;
  avatar_url?: string;
  photo_url?: string;
  client_names?: string[];
}

function formatContactLabel(contact: EmailContactPickerContact): string {
  return contact.name?.trim() || contact.company?.trim() || contact.email;
}

export default function EmailContactPickerModal({
  isOpen,
  onClose,
  onAddRecipients,
  title = 'Add recipients',
  description,
  footerIdleHint,
  addButtonLabel = 'Add recipients',
  defaultTab = 'crm',
  allowedTabs = ['crm', 'company', 'personal'],
}: EmailContactPickerModalProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  const normalizedAllowedTabs = useMemo(
    () => (allowedTabs.length > 0 ? allowedTabs : ['crm']) as ContactPickerTab[],
    [allowedTabs],
  );
  const [activeTab, setActiveTab] = useState<ContactPickerTab>(defaultTab);
  const [crmContacts, setCrmContacts] = useState<EmailContactPickerContact[]>([]);
  const [companyContacts, setCompanyContacts] = useState<EmailContactPickerContact[]>([]);
  const [personalContacts, setPersonalContacts] = useState<EmailContactPickerContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [crmContactTypeFilter, setCrmContactTypeFilter] = useState<string>('all');

  useEffect(() => {
    if (!isOpen) return;
    setActiveTab(normalizedAllowedTabs.includes(defaultTab) ? defaultTab : normalizedAllowedTabs[0]);
    setSearch('');
    setSelectedEmails(new Set());
    setCrmContactTypeFilter('all');
  }, [defaultTab, isOpen, normalizedAllowedTabs]);

  const loadContacts = useCallback(async (tab: ContactPickerTab) => {
    setLoading(true);
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;

      const action = tab === 'crm' ? 'getCrmContacts' : tab === 'company' ? 'getCompanyUsers' : 'getMyContacts';
      const body = tab === 'crm'
        ? { action, limit: 150 }
        : { action, limit: 150 };

      const response = await fetch('/api/v1/email-contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens.access_token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Failed to load ${tab} contacts`);
      }

      const json = await response.json();
      if (!json.success) {
        throw new Error(json.error || `Failed to load ${tab} contacts`);
      }

      if (tab === 'crm') {
        const crmData = json.data || {};
        const contacts = Array.isArray(crmData.contacts) ? crmData.contacts : [];
        const people = Array.isArray(crmData.contactPeople) ? crmData.contactPeople : [];
        const merged: EmailContactPickerContact[] = ([...contacts, ...people] as RawEmailContact[]).map((contact) => ({
          id: String(contact.id),
          email: contact.email,
          name: contact.name || [contact.first_name, contact.last_name].filter(Boolean).join(' ') || contact.company,
          company: contact.company,
          job_title: contact.job_title,
          contact_type: contact.contact_type,
          avatar_url: contact.avatar_url || contact.photo_url,
          source: 'crm',
        }));
        setCrmContacts(merged);
      } else if (tab === 'company') {
        const companyData = Array.isArray(json.data) ? json.data : [];
        setCompanyContacts((companyData as RawEmailContact[]).map((contact) => ({
          id: String(contact.id),
          email: contact.email,
          name: contact.name || [contact.first_name, contact.last_name].filter(Boolean).join(' '),
          company: Array.isArray(contact.client_names) ? contact.client_names.join(', ') : undefined,
          job_title: contact.job_title,
          avatar_url: contact.avatar_url,
          source: 'company',
        })));
      } else {
        const personalData = Array.isArray(json.data) ? json.data : [];
        setPersonalContacts((personalData as RawEmailContact[]).map((contact) => ({
          id: String(contact.id),
          email: contact.email,
          name: contact.name || [contact.first_name, contact.last_name].filter(Boolean).join(' '),
          company: contact.company,
          job_title: contact.job_title,
          avatar_url: contact.avatar_url || contact.photo_url,
          source: 'personal',
        })));
      }
    } catch (error) {
      console.error('[EmailContactPickerModal] load error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    if (activeTab === 'crm' && crmContacts.length === 0) {
      void loadContacts('crm');
    } else if (activeTab === 'company' && companyContacts.length === 0) {
      void loadContacts('company');
    } else if (activeTab === 'personal' && personalContacts.length === 0) {
      void loadContacts('personal');
    }
  }, [activeTab, companyContacts.length, crmContacts.length, isOpen, loadContacts, personalContacts.length]);

  const crmContactTypes = useMemo(() => {
    const types = new Set<string>();
    crmContacts.forEach((contact) => {
      if (contact.contact_type) {
        types.add(contact.contact_type);
      }
    });
    return Array.from(types).sort();
  }, [crmContacts]);

  const visibleContacts = useMemo(() => {
    const source = activeTab === 'crm'
      ? crmContacts
      : activeTab === 'company'
        ? companyContacts
        : personalContacts;

    let filtered = source;
    if (activeTab === 'crm' && crmContactTypeFilter !== 'all') {
      filtered = filtered.filter((contact) => contact.contact_type === crmContactTypeFilter);
    }

    if (!search.trim()) {
      return filtered;
    }

    const query = search.trim().toLowerCase();
    return filtered.filter((contact) =>
      contact.email.toLowerCase().includes(query)
      || contact.name?.toLowerCase().includes(query)
      || contact.company?.toLowerCase().includes(query)
      || contact.job_title?.toLowerCase().includes(query)
      || contact.contact_type?.toLowerCase().includes(query),
    );
  }, [activeTab, companyContacts, crmContactTypeFilter, crmContacts, personalContacts, search]);

  const toggleContact = useCallback((email: string) => {
    setSelectedEmails((current) => {
      const next = new Set(current);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });
  }, []);

  const handleAdd = useCallback(() => {
    if (selectedEmails.size === 0) return;
    const allContacts = [...crmContacts, ...companyContacts, ...personalContacts];
    const recipients = allContacts
      .filter((contact) => selectedEmails.has(contact.email))
      .map((contact) => ({ email: contact.email, name: formatContactLabel(contact) }));

    onAddRecipients(recipients);
    onClose();
  }, [companyContacts, crmContacts, onAddRecipients, onClose, personalContacts, selectedEmails]);

  if (!isOpen || typeof document === 'undefined') return null;

  const subtitleText =
    description !== undefined
      ? description
      : normalizedAllowedTabs.length === 1 && normalizedAllowedTabs[0] === 'crm'
        ? 'Choose one or more CRM contacts to add them to this email.'
        : 'Full CRM access is one click away, with team and personal contacts along for the ride.';

  const tabFilterOptions = (
    [
      { id: 'crm' as const, label: 'CRM' },
      { id: 'company' as const, label: 'Team' },
      { id: 'personal' as const, label: 'Personal' },
    ] satisfies { id: ContactPickerTab; label: string }[]
  ).filter((tab) => normalizedAllowedTabs.includes(tab.id));

  return createPortal(
    <div
      className="fixed inset-0 z-[150] flex justify-end bg-black/30 backdrop-blur-sm animate-in fade-in duration-200"
      style={{ pointerEvents: 'auto' }}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={cn(
          'flex h-full w-full max-w-2xl flex-col shadow-2xl animate-in slide-in-from-right duration-300',
          isDark ? 'bg-gray-900' : 'bg-white',
        )}
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header — shared centered-icon + filter-bar modal chrome */}
        <CapacitorModalFilterHeader
          icon={UserPlus}
          title={title}
          isDark={isDark}
          onClose={onClose}
          applyTopSafeArea={isCapacitor()}
          subtitle={subtitleText}
          filterBar={
            <ModalFilterBar
              isDark={isDark}
              search={{
                value: search,
                onChange: setSearch,
                placeholder:
                  activeTab === 'crm'
                    ? 'Search CRM contacts…'
                    : activeTab === 'company'
                      ? 'Search team members…'
                      : 'Search personal contacts…',
                alwaysVisible: true,
              }}
              filters={
                tabFilterOptions.length > 1
                  ? {
                      options: tabFilterOptions.map((tab) => ({ value: tab.id, label: tab.label })),
                      value: activeTab,
                      onChange: (value) => setActiveTab(value as ContactPickerTab),
                      dropdownLabel: 'Contact source',
                    }
                  : undefined
              }
            >
              {activeTab === 'crm' && crmContactTypes.length > 0 && (
                <select
                  value={crmContactTypeFilter}
                  onChange={(event) => setCrmContactTypeFilter(event.target.value)}
                  className={cn(
                    'h-10 flex-shrink-0 rounded-xl border px-2 text-xs font-medium shadow-sm outline-none transition-colors',
                    isDark
                      ? 'border-slate-700/70 bg-slate-800/80 text-slate-300'
                      : 'border-slate-200/80 bg-white/80 text-slate-600',
                  )}
                  aria-label="Filter CRM contacts by type"
                >
                  <option value="all">All CRM types</option>
                  {crmContactTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              )}
            </ModalFilterBar>
          }
        />

        <div className="min-h-0 flex-1 px-3 py-3 sm:px-5">
          <div className="mx-auto h-full w-full max-w-3xl rounded-2xl border border-gray-200 dark:border-gray-800 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500 dark:text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading contacts...
              </div>
            ) : visibleContacts.length === 0 ? (
              <div className="py-16 text-center text-sm text-gray-500 dark:text-gray-400">
                No contacts found for this view.
              </div>
            ) : (
              visibleContacts.map((contact) => {
                const selected = selectedEmails.has(contact.email);
                return (
                  <button
                    key={`${contact.source}-${contact.id}-${contact.email}`}
                    type="button"
                    onClick={() => toggleContact(contact.email)}
                    className={cn(
                      'w-full px-4 py-3 text-left flex items-start gap-3 transition-colors',
                      selected
                        ? 'bg-blue-50 dark:bg-blue-900/20'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-800/70',
                    )}
                  >
                    <div className={cn(
                      'mt-0.5 w-5 h-5 rounded border flex items-center justify-center shrink-0',
                      selected
                        ? 'border-blue-600 bg-blue-600 text-white'
                        : 'border-gray-300 dark:border-gray-600 text-transparent',
                    )}>
                      <CheckSquare className="w-3.5 h-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{formatContactLabel(contact)}</span>
                        {contact.contact_type && (
                          <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            {contact.contact_type}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">{contact.email}</div>
                      {(contact.company || contact.job_title) && (
                        <div className="text-xs text-gray-400 dark:text-gray-500 truncate mt-1">
                          {[contact.company, contact.job_title].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div
          className="flex-shrink-0 border-t border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-800 dark:bg-gray-800/50 sm:px-6 sm:py-4"
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))' }}
        >
          <div className="mx-auto flex w-full max-w-3xl items-center justify-between gap-3">
            <div className="min-w-0 truncate text-xs text-gray-500 dark:text-gray-400">
              {selectedEmails.size > 0
                ? `${selectedEmails.size} recipient${selectedEmails.size === 1 ? '' : 's'} selected`
                : (footerIdleHint ?? 'Select one or more contacts to add them to the email')}
            </div>
            <div className="flex flex-shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAdd}
                disabled={selectedEmails.size === 0}
                className="px-5 py-2.5 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {addButtonLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}